import { KonvaEventObject } from 'konva/lib/Node'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Group, Layer, Line, Rect, Stage, Text } from 'react-konva'
import { RENDER_CONSTS } from '../config/constants'
import { FightData, Spell, SpellAssignment } from '../redux/models'
import { secondsToTimestamp } from '../util/time-utils'
import { assertDefined, getListFor } from '../util/utils'

type Props = {
    fightData: FightData
    hiddenActorsCount: number

    onAssignmentMoved: (payload: {
        assignmentId: string
        offsetSec: number
    }) => void

    onCreateAssignment: (payload: {
        spellId: string
        offsetSec: number
    }) => void

    onRightClickAssignment: (payload: {
        spellId: string
        assignmentId: string
    }) => void
}

type DragState = {
    spellId: string | undefined
    assignmentId: string | undefined
    dragging: boolean
    xCord: number
    length: number
}

export const TimelineCanvas = (props: Props): JSX.Element | null => {

    const ref = useRef<HTMLDivElement>(null)
    const [stageMaxWidth, setStageMaxWidth] = useState(0)

    const [dragState, setDragState] = useState<DragState>({
        spellId: undefined,
        assignmentId: undefined,
        dragging: false,
        xCord: 0,
        length: 0
    })

    useLayoutEffect(() => {
        if (ref.current) {
            setStageMaxWidth(ref.current.getBoundingClientRect().width)
        }
    }, [])

    const fightId = props.fightData.fight.id
    const [interactive, setInteractive] = useState(false)

    /**
     * This ugly hack seems to increase performance. The idea is that
     * we disable interactivity in the canvas while we're redrawing
     * everything, then we enable it again.
     * 
     * Doing this seems to increase performance while switching fights.
     */
    useEffect(() => {
        setInteractive(false)
        setTimeout(() => {
            setInteractive(true)
        }, 500)
    }, [fightId])

    let rowIndex = 0
    const spellLaneElements: JSX.Element[] = props.fightData.actors.flatMap(actor => {

        const spells = getListFor(props.fightData.spells, (spell) => spell.actorId === actor.id)

        const actorRow = (
            <SpellLane
                key={actor.id}
                assignments={[]}

                isDragged={false}
                row={rowIndex++}
                maxWidth={stageMaxWidth}

                onDragEnd={() => null}
                onDragStart={() => null}
                onDragMove={() => null}

                onCreateAssignment={() => null}
                onRightClickAssignment={() => null}
            />
        )

        const spellRows = spells.map(spell => {

            const assignments = getListFor(props.fightData.assignments, (assign) => assign.spellId === spell.id)

            return (
                <SpellLane
                    key={spell.id}

                    isDragged={dragState.spellId === spell.id}
                    row={rowIndex++}
                    maxWidth={stageMaxWidth}
                    spell={spell}
                    assignments={assignments}

                    onDragEnd={(assignmentId) => {
                        setDragState({
                            ...dragState,
                            dragging: false,
                            assignmentId: undefined,
                            spellId: undefined
                        })

                        props.onAssignmentMoved({
                            assignmentId,
                            offsetSec: dragState.xCord / 2
                        })
                    }}

                    onDragStart={(spellId, assignmentId) => {
                        setDragState({
                            ...dragState,
                            dragging: true,
                            spellId,
                            assignmentId
                        })
                    }}

                    onDragMove={(xCord, length) => setDragState({
                        ...dragState,
                        xCord: xCord,
                        length: length
                    })}

                    onCreateAssignment={(spellId, offsetSec) => {
                        props.onCreateAssignment({
                            spellId,
                            offsetSec
                        })
                    }}

                    onRightClickAssignment={(spellId, assignmentId) => {
                        props.onRightClickAssignment({
                            spellId,
                            assignmentId
                        })
                    }}
                />
            )
        })

        return [actorRow, ...spellRows]

    })

    const ROW_COUNT = spellLaneElements.length + props.hiddenActorsCount

    const timelineTickElements: JSX.Element[] = []
    const spacing = 60

    for (let index = 0; index < 31; index++) {

        if (index === 0) {
            continue
        }

        const bigTick = (index !== 0 && index % 2 === 0)

        timelineTickElements.push(
            <Group key={index}>
                <Line
                    y={20}
                    x={spacing * index}
                    stroke={"silver"}
                    strokeWidth={1}
                    transformsEnabled={'position'}
                    points={[
                        0.5,
                        (bigTick ? 0 : 5),
                        0.5,
                        RENDER_CONSTS.ROW_HEIGHT * ROW_COUNT + 10
                    ]}
                    opacity={0.7}
                />
                {bigTick &&
                    <Text
                        y={5}
                        x={spacing * index - 15}
                        text={secondsToTimestamp(30 * index)}
                        fontVariant={"bold"}
                        fontFamily={"Consolas"}
                    />
                }
            </Group>
        )

    }

    return (
        <div
            ref={ref}
            className="bg-white border border-dark flex-grow-1"
        >
            <Stage
                id="timeline-stage"
                style={{
                    overflow: "hidden"
                }}
                height={(RENDER_CONSTS.ROW_HEIGHT * ROW_COUNT) + RENDER_CONSTS.LANES_Y_OFFSET}

                // This -1 takes care of the right border against the spell-lane
                width={stageMaxWidth - 1}
                listening={interactive}
            >

                <Layer y={RENDER_CONSTS.LANES_Y_OFFSET}>

                    <Group y={-RENDER_CONSTS.LANES_Y_OFFSET} >
                        {timelineTickElements}
                    </Group>

                    {spellLaneElements}

                    <Rect
                        name="spell-lane-top-border"
                        y={1}
                        x={0}
                        width={stageMaxWidth}
                        height={1}
                        fill={"black"}
                    />

                    {dragState.dragging &&
                        <>
                            <Rect
                                name="drag-ruler"
                                y={1}
                                x={dragState.xCord}
                                width={1}
                                height={9000}
                                fill={"black"}
                            />
                            <Rect
                                name="drag-ruler"
                                y={1}
                                x={dragState.xCord + dragState.length}
                                width={1}
                                height={9000}
                                fill={"black"}
                            />
                        </>
                    }

                </Layer>

            </Stage>
        </div>
    )
}

type SpellLaneProps = {
    spell?: Spell
    assignments: SpellAssignment[]
    row: number
    maxWidth: number
    isDragged: boolean
    onCreateAssignment: (spellId: string, offsetSec: number) => void
    onRightClickAssignment: (spellId: string, assignmentId: string) => void
}

const SpellLane = (props: SpellLaneProps & DragProps): JSX.Element | null => {

    const { row, maxWidth, spell, onCreateAssignment, onRightClickAssignment, assignments, ...dragProps } = props

    return (
        <Group
            y={RENDER_CONSTS.ROW_HEIGHT * row}
            clip={{
                x: 0,
                y: 1,
                width: maxWidth,
                height: RENDER_CONSTS.ROW_HEIGHT,
            }}
        >
            {/* Drag background */}
            {dragProps.isDragged &&
                <Rect
                    x={-0.5}
                    y={0.5}
                    width={maxWidth}
                    height={RENDER_CONSTS.ROW_HEIGHT}
                    fill={'#000'}
                    opacity={0.05}
                    onDblClick={(e) => {
                        console.log(e)
                    }}
                />
            }

            {/* This is the background element */}
            <Rect
                x={-0.5}
                y={0.5}
                width={maxWidth}
                height={RENDER_CONSTS.ROW_HEIGHT}
                stroke={'black'}
                opacity={1}
                strokeWidth={1}
                onDblClick={(e) => {
                    if (spell) {
                        onCreateAssignment(spell.id, (e.evt as any).layerX / 2)
                    }
                }}
            />

            {spell && assignments.map(assignment => {
                return (
                    <SpellRect
                        key={spell.id + assignment.offset}
                        {...dragProps}
                        spellId={spell.id}
                        assignmentId={assignment.id}

                        maxWidth={maxWidth}

                        rampDuration={assignment.overrideRamp ?? spell.ramp}
                        cooldown={assignment.overrideCooldown ?? spell.cooldown}
                        duration={assignment.overrideDuration ?? spell.duration}

                        start={assignment.offset}
                        color={spell.color}
                        hideTimestamp={assignment.hideTimestamp}
                        onRightClickAssignment={onRightClickAssignment}
                    />
                )
            })}

        </Group>
    )
}

type DragProps = {
    onDragStart: (spellId: string, assignmentId: string) => void
    onDragEnd: (assignmentId: string) => void
    onDragMove: (xCord: number, length: number) => void
}

type SpellRectProps = {
    spellId: string
    assignmentId: string
    maxWidth: number
    color?: string
    cooldown: number
    duration: number
    start: number
    hideTimestamp?: boolean
    rampDuration?: number
    onRightClickAssignment: (spellId: string, assignmentId: string) => void
}

const SpellRect = (props: SpellRectProps & DragProps): JSX.Element | null => {

    const {
        maxWidth, duration, cooldown,
        start, color, onDragStart, onDragMove, onDragEnd,
        hideTimestamp = false,
        rampDuration = 0,
        spellId, assignmentId,
        onRightClickAssignment
    } = props

    const [xCord, setXCord] = useState(start * 2)

    const onMouseEnterHandler = (e: KonvaEventObject<MouseEvent>) => {
        const stage = e.target.getStage()
        assertDefined(stage)
        stage.container().style.cursor = "pointer"
    }

    const onMouseLeaveHandler = (e: KonvaEventObject<MouseEvent>) => {
        const stage = e.target.getStage()
        assertDefined(stage)
        stage.container().style.cursor = "default"
    }

    return (
        <Group
            x={xCord}
            name="spell-rect"
            draggable
            onDragMove={(e) => {
                e.target.y(0.5)

                const minX = 0
                const maxX = maxWidth - (
                    e.target.getClientRect().width - (e.target.getClientRect().width - 20)
                )

                let targetX = e.target.x()

                if (e.target.x() <= minX) {
                    targetX = minX
                } else if (e.target.x() >= maxX) {
                    targetX = maxX
                }

                // Make sure we always end on an even position,
                // ensures we don't end up between pretzels
                if ((targetX % 2) !== 0) {
                    targetX -= -1
                }

                // Don't remove this, it's what's stopping us from
                // going off the stage
                e.target.x(targetX)

                setXCord(targetX)
                onDragMove(targetX, cooldown * 2)
            }}
            onWheel={(e) => {
                e.evt.preventDefault()

                if (e.evt.shiftKey === false) {
                    return
                }

                // TODO: Add min-max clamping

                let targetX = xCord

                if (e.evt.deltaY > 0)
                    targetX = xCord - (e.evt.altKey ? 1 : 2)
                if (e.evt.deltaY <= 0)
                    targetX = xCord + (e.evt.altKey ? 1 : 2)

                setXCord(targetX)
                onDragMove(targetX, cooldown * 2)
            }}
            onDragStart={() => {
                onDragStart(spellId, assignmentId)
            }}
            onDragEnd={(e) => {
                setXCord(e.target.x())
                onDragEnd(assignmentId)
            }}
            onMouseEnter={() => {
                onDragStart(spellId, assignmentId)
            }}
            onMouseOver={() => {
                onDragMove(xCord, cooldown * 2)
            }}
            onMouseLeave={() => {
                onDragEnd(assignmentId)
            }}
            onContextMenu={(e) => {
                e.evt.preventDefault()
                onRightClickAssignment(spellId, assignmentId)
            }}
        >
            {/* Duration + Cooldown */}
            <Rect
                x={0.0}
                y={0.5}
                width={(cooldown * 2) + 0}
                height={RENDER_CONSTS.ROW_HEIGHT - 0.5}
                fill={color ?? 'black'}
                opacity={0.3}
                onMouseEnter={onMouseEnterHandler}
                onMouseLeave={onMouseLeaveHandler}
            />

            {/* Active duration */}
            <Rect
                x={0.0}
                y={0.5}
                width={(duration * 2) + 0}
                height={RENDER_CONSTS.ROW_HEIGHT - 0.5}
                fill={color ?? 'black'}
                opacity={0.3}
                onMouseEnter={onMouseEnterHandler}
                onMouseLeave={onMouseLeaveHandler}
            />

            {/* Ramp duration */}
            {rampDuration > 0 &&
                <Rect
                    x={-(rampDuration * 2)}
                    y={0.5 + ((RENDER_CONSTS.ROW_HEIGHT - 0.5) / 2)}
                    width={(rampDuration * 2) + 0}
                    height={(RENDER_CONSTS.ROW_HEIGHT - 0.5) / 2}
                    fill={color ?? 'black'}
                    opacity={0.2}
                    onMouseEnter={onMouseEnterHandler}
                    onMouseLeave={onMouseLeaveHandler}
                />
            }

            {!hideTimestamp &&
                <Text
                    fill={"white"}
                    x={5}
                    y={1.5}
                    text={secondsToTimestamp(xCord / 2)}
                    align={"center"}
                    verticalAlign={"middle"}
                    fontVariant={"bold"}
                    fontFamily={"Consolas"}
                    height={RENDER_CONSTS.ROW_HEIGHT - 0.5}
                // shadowBlur={3}
                // shadowColor={"black"}
                // shadowOpacity={1}
                />
            }
        </Group>
    )
}